import React, { useState, useCallback, lazy, Suspense } from "react";
import { PostMediaFile } from "shared/types/post";
import { convertCustomerUploadsS3ToCloudFront } from "libs/media-files";
import { uploadEditedVideo, VideoUploadProgress } from "../utils/video-upload";

const CreativeVideoEditorModal = lazy(() =>
  import("ui/video-editor").then((m) => ({
    default: m.CreativeVideoEditorModal,
  }))
);

interface Props {
  children(params: { onEdit(file: PostMediaFile): void }): React.ReactNode;
  onChange(file: PostMediaFile): void;
}

export const PostVideoEditing: React.FC<Props> = ({ children, onChange }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<PostMediaFile | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleEdit = useCallback((file: PostMediaFile) => {
    setCurrentVideo(file);
    setIsModalOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    setCurrentVideo(null);
    setProgress(null);
    setIsSaving(false);
  }, []);

  const handleSave = useCallback(
    async (videoBlob: Blob) => {
      if (!currentVideo) return;

      try {
        setIsSaving(true);
        setProgress(0);

        const editedVideo = await uploadEditedVideo({
          videoBlob,
          previousFile: currentVideo,
          onProgress: (progressInfo: VideoUploadProgress) => {
            // Convert phase progress to overall progress
            let overallProgress = 0;
            switch (progressInfo.phase) {
              case "thumbnail": {
                overallProgress = progressInfo.progress * 0.3; // 30% for thumbnail
                break;
              }
              case "video": {
                overallProgress = 30 + progressInfo.progress * 0.7; // 70% for video
                break;
              }
              case "complete": {
                overallProgress = 100;
                break;
              }
            }
            setProgress(overallProgress);
          },
        });

        // Call onChange with the edited video
        onChange(editedVideo);

        // Close modal after successful upload
        handleClose();
      } catch (error) {
        console.error("Error saving edited video:", error);
        setProgress(null);
        // Keep modal open so user can try again
      } finally {
        setIsSaving(false);
      }
    },
    [currentVideo, onChange, handleClose]
  );

  return (
    <>
      {children({ onEdit: handleEdit })}
      {isModalOpen && (
        <Suspense fallback={null}>
          <CreativeVideoEditorModal
            video={convertCustomerUploadsS3ToCloudFront(
              currentVideo?.url || ""
            )}
            assetId={currentVideo?._id || ""}
            assetUrl={
              currentVideo?.originalUrl ||
              currentVideo?.origUrl ||
              currentVideo?.url ||
              ""
            }
            isReplacingOriginal={false}
            show={isModalOpen}
            onClose={handleClose}
            onSave={handleSave}
            progress={progress}
            message="This video is being used in an upcoming post. You are editing the version that will be published."
            saveButtonText="Save"
            isSaving={isSaving}
          />
        </Suspense>
      )}
    </>
  );
};
